import {
  ArrowLeftIcon,
  CubeIcon,
  HeartIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import {
  Button,
  ButtonGroup,
  Card,
  Chip,
  Divider,
  Image,
  Select,
  SelectItem,
  Spinner,
  Textarea,
} from '@heroui/react';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DeliveryEstimate from '../components/DeliveryEstimate';
import SEOHead from '../components/SEOHead';
import { useCart } from '../contexts/CartContext';
import { useLocale } from '../contexts/LocaleContext';
import { productService } from '../services/productService';
import {
  BundleProduct,
  MaterialOption,
  PriceCalculation,
  ProcessingExtra,
  ProductImage,
  ProductPart,
} from '../types/product';
import { createSafeHtmlProps } from '../utils/htmlUtils';
import {
  createProductUrl,
  formatPriceForMeta,
  generateCanonicalUrl,
  generateProductBreadcrumbs,
  generateProductKeywords,
  generateProductMetaDescription,
  mapStockStatusToAvailability,
} from '../utils/seoUtils';

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [quantityInput, setQuantityInput] = useState('1');
  const quantity = parseInt(quantityInput, 10) || 0;
  const [selectedMaterialId, setSelectedMaterialId] = useState<string>('');
  const [selectedExtraId, setSelectedExtraId] = useState<string>('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [priceCalculation, setPriceCalculation] =
    useState<PriceCalculation | null>(null);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [isSafetyOpen, setIsSafetyOpen] = useState(false);
  const { addToCart, isLoading: cartLoading } = useCart();
  const { formatCurrency, convertPrice, currentLocale } = useLocale();
  const formatAmount = (amount: number) => formatCurrency(convertPrice(amount));
  const countryCode = currentLocale.preferredCountryCode;

  const {
    data: product,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['product', id, countryCode],
    queryFn: () => productService.getProduct(id!, countryCode),
    enabled: !!id,
  });

  // Set default material when product loads
  useEffect(() => {
    if (
      product &&
      Array.isArray(product.materialOptions) &&
      product.materialOptions.length > 0 &&
      !selectedMaterialId
    ) {
      const defaultMaterial =
        product.materialOptions.find((m: MaterialOption) => m.isDefault) ||
        product.materialOptions[0];
      setSelectedMaterialId(defaultMaterial.id);
    }
  }, [product, selectedMaterialId]);

  // Set default processing extra when product loads (if only one available)
  useEffect(() => {
    if (
      product &&
      Array.isArray(product.processingExtras) &&
      product.processingExtras.length === 1 &&
      !selectedExtraId
    ) {
      const availableExtras = product.processingExtras.filter(
        (extra: ProcessingExtra) => extra.isAvailable
      );
      if (availableExtras.length === 1) {
        setSelectedExtraId(availableExtras[0].id);
      }
    }
  }, [product, selectedExtraId]);

  // Calculate price when configuration changes
  useEffect(() => {
    const calculatePrice = async () => {
      if (!product || !selectedMaterialId) {
        setPriceCalculation(null);
        return;
      }

      setIsCalculatingPrice(true);
      setPriceCalculation(null);
      try {
        const response = await productService.calculatePrice({
          productId: product.id,
          selectedMaterialId,
          selectedExtraIds: selectedExtraId ? [selectedExtraId] : [],
          quantity: Math.max(1, quantity),
        });
        setPriceCalculation(response);
      } catch (error) {
        // Fallback to base price calculation
        try {
          const selectedMaterial = product.materialOptions?.find(
            (m: MaterialOption) => m.id === selectedMaterialId
          );
          const selectedExtras = selectedExtraId
            ? (product.processingExtras || []).filter(
                (e: ProcessingExtra) => e.id === selectedExtraId
              )
            : [];

          if (!selectedMaterial) {
            return;
          }

          const basePrice = product.basePrice || 0;
          const materialModifier = selectedMaterial.priceModifier || 0;
          const extrasTotal = selectedExtras.reduce(
            (sum: number, extra: ProcessingExtra) => sum + (extra.price || 0),
            0
          );
          const unitPrice = basePrice + materialModifier + extrasTotal;

          setPriceCalculation({
            basePrice,
            materialPriceModifier: materialModifier,
            extrasTotal,
            unitPrice,
            totalPrice: unitPrice * Math.max(1, quantity),
            materialName: selectedMaterial.displayName || '',
            selectedExtras: selectedExtras.map((e: ProcessingExtra) => ({
              id: e.id,
              displayName: e.displayName || '',
              price: e.price || 0,
              processingTimeDays: e.processingTimeDays || 0,
              requiresManualProcessing: e.requiresManualProcessing || false,
            })),
          });
        } catch (fallbackError) {
          // Set a basic price calculation as last resort
          setPriceCalculation({
            basePrice: product.basePrice || 0,
            materialPriceModifier: 0,
            extrasTotal: 0,
            unitPrice: product.basePrice || 0,
            totalPrice: (product.basePrice || 0) * Math.max(1, quantity),
            materialName: 'Standard',
            selectedExtras: [],
          });
        }
      } finally {
        setIsCalculatingPrice(false);
      }
    };

    calculatePrice();
  }, [product, selectedMaterialId, selectedExtraId, quantity]);

  const handleAddToCart = async () => {
    if (id && selectedMaterialId) {
      try {
        if (quantity <= 0) return;
        await addToCart(
          id,
          quantity,
          selectedMaterialId,
          selectedExtraId ? [selectedExtraId] : [],
          specialInstructions || undefined
        );
        navigate('/cart');
      } catch (error) {
        // Error handled by toast notification in addToCart
      }
    }
  };

  const handleToggleFavorite = () => {
    setIsFavorite(!isFavorite);
    // TODO: Implement favorites functionality
  };

  const handleExtraChange = (extraId: string) => {
    setSelectedExtraId(extraId);
  };

  if (isLoading) {
    return (
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <SEOHead
          title='Loading Product...'
          description='Loading product details from CodedGhost Minis premium 3D printed miniatures collection.'
        />
        <div className='flex justify-center items-center py-20'>
          <Spinner size='lg' color='primary' />
          <span className='ml-3 text-gray-600'>
            Loading miniature details...
          </span>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <SEOHead
          title='Product Not Found'
          description="The miniature you're looking for doesn't exist or has been removed from our catalog."
        />
        <div className='text-center py-20'>
          <div className='text-6xl mb-4'>😕</div>
          <h2 className='text-2xl font-bold text-gray-900 mb-4'>
            Miniature Not Found
          </h2>
          <p className='text-gray-600 mb-6'>
            The miniature you're looking for doesn't exist or has been removed.
          </p>
          <Button
            color='primary'
            startContent={<ArrowLeftIcon className='w-4 h-4' />}
            onPress={() => navigate('/search')}
          >
            Back to Catalog
          </Button>
        </div>
      </div>
    );
  }

  const images = product.images?.length > 0 ? product.images : [];
  const mainImage =
    product.mainImageUrl || (images.length > 0 ? images[0].url : null);
  const displayImages: ProductImage[] = mainImage
    ? [
        {
          url: mainImage,
          name: product.name,
          description: '',
          width: 0,
          height: 0,
        },
        ...images,
      ]
    : images;

  const selectedMaterial = product.materialOptions?.find(
    (m: MaterialOption) => m.id === selectedMaterialId
  );
  const safetyHtml = (
    selectedMaterial?.description ||
    selectedMaterial?.processingNotes ||
    ''
  ).trim();

  // Generate SEO data
  const metaDescription = generateProductMetaDescription(
    product.name,
    product.category,
    product.material,
    formatPriceForMeta(product.startingPrice),
    product.description
  );

  const keywords = generateProductKeywords(
    product.name,
    product.category,
    product.material,
    product.creator,
    product.scale
  );

  const breadcrumbs = generateProductBreadcrumbs(
    product.name,
    product.category,
    product.id
  );

  const canonicalUrl = generateCanonicalUrl(
    createProductUrl(product.id, product.name)
  );

  const availability = mapStockStatusToAvailability(product.stockStatusDisplay);

  return (
    <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      <SEOHead
        title={product.name}
        description={metaDescription}
        keywords={keywords}
        image={mainImage || undefined}
        url={canonicalUrl}
        type='product'
        price={formatPriceForMeta(product.startingPrice)}
        availability={availability}
        category={product.category}
        breadcrumbs={breadcrumbs}
      />

      {/* Breadcrumb */}
      <div className='mb-6'>
        <Button
          variant='light'
          startContent={<ArrowLeftIcon className='w-4 h-4' />}
          onPress={() => navigate('/search')}
          className='mb-4'
        >
          Back to Catalog
        </Button>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
        {/* Product Images */}
        <div className='space-y-4'>
          {/* Main Image */}
          <div className='aspect-square'>
            {displayImages.length > 0 ? (
              <Image
                src={displayImages[selectedImageIndex]?.url}
                alt={product.name}
                className='w-full h-full object-cover rounded-lg'
              />
            ) : (
              <div className='w-full h-full bg-gray-200 flex items-center justify-center rounded-lg text-6xl'>
                🎲
              </div>
            )}
          </div>

          {/* Image Thumbnails */}
          {displayImages.length > 1 && (
            <div className='grid grid-cols-4 gap-2'>
              {displayImages.map((image, index: number) => (
                <div
                  key={index}
                  className={`aspect-square cursor-pointer rounded-lg overflow-hidden border-2 ${
                    selectedImageIndex === index
                      ? 'border-primary'
                      : 'border-transparent'
                  }`}
                  onClick={() => setSelectedImageIndex(index)}
                >
                  <Image
                    src={image.url}
                    alt={`${product.name} ${index + 1}`}
                    className='w-full h-full object-cover'
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className='space-y-6'>
          {/* Title and Price */}
          <div>
            <div className='flex items-start justify-between mb-2'>
              <h1 className='text-3xl font-bold text-gray-900'>
                {product.name}
              </h1>
              <Button
                isIconOnly
                variant='light'
                color={isFavorite ? 'danger' : 'default'}
                onPress={handleToggleFavorite}
              >
                {isFavorite ? (
                  <HeartIconSolid className='w-6 h-6' />
                ) : (
                  <HeartIcon className='w-6 h-6' />
                )}
              </Button>
            </div>

            {/* Price Display (static base price to avoid flicker) */}
            <div className='mb-4'>
              <div className='text-3xl font-bold text-primary'>
                {formatAmount(product.startingPrice)}
              </div>
            </div>

            {/* Tags */}
            <div className='flex flex-wrap gap-2 mb-4'>
              {product.isFeatured && (
                <Chip color='warning' variant='solid'>
                  Featured
                </Chip>
              )}
              {product.isAvailable ? (
                <Chip color='success' variant='flat'>
                  In Stock
                </Chip>
              ) : (
                <Chip color='danger' variant='flat'>
                  Out of Stock
                </Chip>
              )}
              {product.category && (
                <Chip color='secondary' variant='flat'>
                  {product.category}
                </Chip>
              )}
            </div>
          </div>

          {/* Material Selection */}
          {Array.isArray(product.materialOptions) &&
            product.materialOptions.length > 0 &&
            (() => {
              const availableMaterials = product.materialOptions.filter(
                (material: MaterialOption) => material.isAvailable
              );

              return (
                <div>
                  <h3 className='text-lg font-semibold text-gray-900 mb-3'>
                    Material Options
                  </h3>
                  {availableMaterials.length === 1 ? (
                    // Single material - show as selected info, no dropdown
                    <div className='p-4 bg-blue-50 border border-blue-200 rounded-lg'>
                      {availableMaterials.map((material: MaterialOption) => (
                        <div
                          key={material.id}
                          className='flex justify-between items-center w-full'
                        >
                          <div>
                            <div className='font-medium text-blue-900'>
                              {material.displayName}
                            </div>
                            <div className='text-sm text-blue-700'>
                              {material.description}
                            </div>
                          </div>
                          <div className='text-sm font-medium text-blue-900'>
                            +{formatAmount(material.priceModifier)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Multiple materials - show dropdown
                    <Select
                      items={availableMaterials}
                      selectedKeys={
                        selectedMaterialId ? [selectedMaterialId] : []
                      }
                      onSelectionChange={keys => {
                        const selected = Array.from(keys)[0] as string;
                        setSelectedMaterialId(selected);
                      }}
                      label='Choose Material'
                      placeholder='Select a material'
                      className='w-full'
                      disallowEmptySelection
                      description='Each material has different price modifiers'
                    >
                      {(material: MaterialOption) => (
                        <SelectItem
                          key={material.id}
                          description={`+${formatAmount(
                            material.priceModifier
                          )}`}
                        >
                          {material.displayName}
                        </SelectItem>
                      )}
                    </Select>
                  )}
                  {/* Show selected material details - only for multiple materials */}
                  {selectedMaterialId && availableMaterials.length > 1 && (
                    <div className='mt-2 p-3 bg-gray-50 rounded-lg'>
                      <div className='text-sm text-gray-600'>
                        {product.materialOptions
                          .filter(
                            (material: MaterialOption) =>
                              material.id === selectedMaterialId
                          )
                          .map((material: MaterialOption) => (
                            <div key={material.id}>
                              <span className='font-medium'>
                                {material.displayName}:
                              </span>{' '}
                              {material.description}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

          {/* Product Safety Information (from CMS, per selected material) */}
          {safetyHtml && (
            <div className='mt-2'>
              <button
                type='button'
                className='w-full flex items-center justify-between p-3 rounded-md border bg-gray-50 hover:bg-gray-100 transition-colors'
                onClick={() => setIsSafetyOpen(prev => !prev)}
                aria-controls='safety-content'
              >
                <span className='text-sm font-medium text-gray-900'>
                  Product Safety Information
                </span>
                <svg
                  className={`w-4 h-4 text-gray-600 transform transition-transform ${
                    isSafetyOpen ? 'rotate-180' : 'rotate-0'
                  }`}
                  viewBox='0 0 20 20'
                  fill='currentColor'
                  aria-hidden='true'
                >
                  <path
                    fillRule='evenodd'
                    d='M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.08z'
                    clipRule='evenodd'
                  />
                </svg>
              </button>
              {isSafetyOpen && (
                <div
                  id='safety-content'
                  className='mt-2 p-4 rounded-md border bg-white prose prose-sm max-w-none'
                  dangerouslySetInnerHTML={{ __html: safetyHtml }}
                />
              )}
            </div>
          )}

          {/* Processing Extras */}
          {Array.isArray(product.processingExtras) &&
            product.processingExtras.length > 0 &&
            (() => {
              const availableExtras = product.processingExtras
                .filter((extra: ProcessingExtra) => extra.isAvailable)
                .sort(
                  (a: ProcessingExtra, b: ProcessingExtra) =>
                    a.sortOrder - b.sortOrder
                );

              return (
                <div>
                  <h3 className='text-lg font-semibold text-gray-900 mb-3'>
                    Processing Extras
                  </h3>
                  {availableExtras.length === 1 ? (
                    // Single processing extra - show as selected info, no dropdown
                    <div className='p-4 bg-green-50 border border-green-200 rounded-lg'>
                      {availableExtras.map((extra: ProcessingExtra) => (
                        <div
                          key={extra.id}
                          className='flex justify-between items-center w-full'
                        >
                          <div>
                            <div className='font-medium text-green-900'>
                              {extra.displayName}
                            </div>
                            <div className='text-sm text-green-700'>
                              {extra.description}
                            </div>
                          </div>
                          <div className='text-sm font-medium text-green-900'>
                            +{formatAmount(extra.price)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Multiple processing extras - show dropdown
                    <Select
                      items={availableExtras}
                      selectedKeys={selectedExtraId ? [selectedExtraId] : []}
                      onSelectionChange={keys => {
                        const selected = Array.from(keys)[0] as string;
                        handleExtraChange(selected);
                      }}
                      label='Choose Processing Extra'
                      placeholder='Select an extra (optional)'
                      className='w-full'
                      description='Processing extras add to the total cost'
                    >
                      {(extra: ProcessingExtra) => (
                        <SelectItem
                          key={extra.id}
                          description={`+${formatAmount(extra.price)}`}
                        >
                          {extra.displayName}
                        </SelectItem>
                      )}
                    </Select>
                  )}
                  {/* Show selected processing extra details - only for multiple extras */}
                  {selectedExtraId && availableExtras.length > 1 && (
                    <div className='mt-2 p-3 bg-gray-50 rounded-lg'>
                      <div className='text-sm text-gray-600'>
                        {product.processingExtras
                          .filter(
                            (extra: ProcessingExtra) =>
                              extra.id === selectedExtraId
                          )
                          .map((extra: ProcessingExtra) => (
                            <div key={extra.id}>
                              <span className='font-medium'>
                                {extra.displayName}:
                              </span>{' '}
                              {extra.description}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

          {/* Special Instructions */}
          <div>
            <h3 className='text-lg font-semibold text-gray-900 mb-3'>
              Special Instructions
            </h3>
            <Textarea
              value={specialInstructions}
              onValueChange={setSpecialInstructions}
              placeholder='Any special requests or modifications...'
              maxRows={3}
            />
          </div>

          {/* Description */}
          <div>
            <h3 className='text-lg font-semibold text-gray-900 mb-2'>
              Description
            </h3>
            <div
              {...createSafeHtmlProps(
                product.description,
                'text-gray-600 leading-relaxed prose prose-sm max-w-none'
              )}
            />
            {product.isBundle && product.bundleProducts?.length > 0 && (
              <div className='mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg'>
                <h4 className='text-sm font-semibold text-purple-900 mb-2'>
                  Bundle Includes
                </h4>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                  {product.bundleProducts.map(
                    (bundleProduct: BundleProduct) => (
                      <Button
                        key={bundleProduct.id}
                        variant='flat'
                        className='justify-start h-auto py-2'
                        onPress={() =>
                          navigate(
                            createProductUrl(
                              bundleProduct.id,
                              bundleProduct.name
                            )
                          )
                        }
                      >
                        <div className='flex items-center gap-3 text-left'>
                          {bundleProduct.mainImageUrl ? (
                            <Image
                              src={bundleProduct.mainImageUrl}
                              alt={bundleProduct.name}
                              width={48}
                              height={48}
                              className='rounded-md object-cover'
                            />
                          ) : (
                            <div className='w-12 h-12 bg-purple-100 rounded-md flex items-center justify-center text-lg'>
                              🎲
                            </div>
                          )}
                          <div>
                            <div className='text-sm font-medium text-gray-900'>
                              {bundleProduct.name}
                            </div>
                            {bundleProduct.description && (
                              <div className='text-xs text-gray-600 line-clamp-2'>
                                {bundleProduct.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </Button>
                    )
                  )}
                </div>
              </div>
            )}
            {product.partCount > 0 && (
              <div className='mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg'>
                <p className='text-sm text-blue-900'>
                  <strong>
                    This product contains {product.partCount}{' '}
                    {product.partCount === 1 ? 'part' : 'parts'}
                  </strong>
                </p>
                {product.parts && product.parts.length > 0 && (
                  <ul className='mt-2 space-y-1'>
                    {product.parts.map((part: ProductPart, index: number) => (
                      <li
                        key={part.id || index}
                        className='text-xs text-blue-700'
                      >
                        • {part.name}
                        {part.description && ` - ${part.description}`}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <Divider />

          {/* Specifications */}
          <div>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>
              Specifications
            </h3>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <Card className='p-4'>
                <div className='flex items-center gap-3'>
                  <CubeIcon className='w-5 h-5 text-gray-500' />
                  <div>
                    <div className='text-sm text-gray-500'>Scale</div>
                    <div className='font-medium'>
                      {product.scale || 'Not specified'}
                    </div>
                  </div>
                </div>
              </Card>

              {(product.lengthMm || product.widthMm || product.heightMm) && (
                <Card className='p-4'>
                  <div className='flex items-center gap-3'>
                    <CubeIcon className='w-5 h-5 text-gray-500' />
                    <div>
                      <div className='text-sm text-gray-500'>Dimensions</div>
                      <div className='font-medium'>
                        {product.dimensionsDisplay ||
                          `${product.lengthMm || '?'}mm × ${
                            product.widthMm || '?'
                          }mm × ${product.heightMm || '?'}mm`}
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {selectedMaterial && (
                <Card className='p-4'>
                  <div className='flex items-center gap-3'>
                    <div className='w-5 h-5 bg-gray-500 rounded-full' />
                    <div>
                      <div className='text-sm text-gray-500'>Material</div>
                      <div className='font-medium'>
                        {selectedMaterial.displayName}
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {product.category && (
                <Card className='p-4'>
                  <div className='flex items-center gap-3'>
                    <div className='w-5 h-5 bg-secondary rounded-full' />
                    <div>
                      <div className='text-sm text-gray-500'>Category</div>
                      <div className='font-medium'>{product.category}</div>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </div>

          <Divider />

          {/* Delivery Estimate */}
          <div>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>
              Delivery Estimate
            </h3>
            <DeliveryEstimate
              product={product}
              selectedMaterialId={selectedMaterialId}
              selectedExtraIds={selectedExtraId ? [selectedExtraId] : []}
              quantity={quantity}
              showTimeline={true}
            />
          </div>

          {/* Add to Cart */}
          {product.isAvailable && (
            <div className='space-y-4'>
              <div className='flex items-center gap-4'>
                <div className='flex items-center'>
                  <span className='text-sm font-medium text-gray-700 mr-3'>
                    Quantity:
                  </span>
                  <ButtonGroup variant='bordered' size='sm'>
                    <Button
                      isIconOnly
                      onPress={() => {
                        const q = parseInt(quantityInput, 10) || 0;
                        const next = Math.max(0, q - 1);
                        setQuantityInput(String(next));
                      }}
                      aria-label='Decrease quantity'
                    >
                      -
                    </Button>
                    <input
                      aria-label='Quantity'
                      inputMode='numeric'
                      pattern='[0-9]*'
                      className='min-w-12 w-16 text-center h-8 border-y border-divider outline-none rounded-none'
                      value={quantityInput}
                      onChange={e => {
                        const v = e.target.value.replace(/[^0-9]/g, '');
                        setQuantityInput(v);
                      }}
                    />
                    <Button
                      isIconOnly
                      onPress={() => {
                        const q = parseInt(quantityInput, 10) || 0;
                        setQuantityInput(String(q + 1));
                      }}
                      aria-label='Increase quantity'
                    >
                      +
                    </Button>
                  </ButtonGroup>
                </div>
              </div>

              <Button
                color='primary'
                size='lg'
                className='w-full'
                startContent={<ShoppingCartIcon className='w-5 h-5' />}
                onPress={handleAddToCart}
                isLoading={cartLoading}
                isDisabled={
                  !selectedMaterialId ||
                  isCalculatingPrice ||
                  !priceCalculation ||
                  quantity <= 0
                }
              >
                Add to Cart -{' '}
                {isCalculatingPrice
                  ? 'Calculating...'
                  : priceCalculation
                    ? formatAmount(priceCalculation.totalPrice)
                    : 'Price unavailable'}
              </Button>
            </div>
          )}

          {/* Unavailable Message */}
          {!product.isAvailable && (
            <div className='p-4 bg-red-50 border border-red-200 rounded-lg'>
              <p className='text-red-800 font-medium'>
                This miniature is currently unavailable.
              </p>
              <p className='text-red-600 text-sm mt-1'>
                Check back later or browse our other miniatures.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
